import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Popover } from "./Popover.tsx";
import { Tooltip } from "./Tooltip.tsx";
import { useClickOutside } from "./useClickOutside.ts";
import { useCloseOnLeave } from "./useCloseOnLeave.ts";

/**
 * Popover exit-animation window — matches `--transition-slow` (0.18s),
 * the same token `.popover`'s CSS enter animation uses. Same reasoning
 * as `Dialog.tsx`'s own `DIALOG_EXIT_MS`: driven from JS as a literal
 * so a `setTimeout` doesn't need a `getComputedStyle` round-trip.
 */
const POPOVER_EXIT_MS = 180;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * The trigger-button+wrapper+Popover shell every toolbar dropdown menu
 * hand-rolled identically (deep-review finding: 8 menus, same ~30-line
 * shell each). `closeOnLeave` defaults on — MapSettingsMenu/SettingsMenu/
 * ElementSearch opt out since a hover-away close doesn't fit a menu you're
 * meant to linger in (checkboxes, a search box).
 */
export function DropdownMenu({
  open,
  onToggle,
  onClose,
  triggerClassName,
  triggerLabel,
  triggerTitle = triggerLabel,
  triggerIcon,
  align = "right",
  popoverClassName = "popover menu-popover",
  closeOnLeave = true,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  triggerClassName: string;
  triggerLabel: string;
  /** Defaults to `triggerLabel` — set separately only when the tooltip should differ from the accessible name (AddElementMenu shows the active tool's own name here). */
  triggerTitle?: string;
  triggerIcon: ReactNode;
  align?: "left" | "right";
  popoverClassName?: string;
  closeOnLeave?: boolean;
  children: ReactNode;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Second-audit finding: the enter animation lands in point 3, but the
  // close path snaps because `useClickOutside`/`useCloseOnLeave`/Escape
  // all directly invoke the parent's `onClose`, which unmounts the
  // Popover the same tick. Wrapping `onClose` in a delayed handler keeps
  // the Popover mounted for one `--transition-slow` window with
  // `popover-exiting` applied, then invokes the real `onClose`. Every
  // hook + Escape below points at `closeWithExit`, not the raw prop.
  const [exiting, setExiting] = useState(false);
  const exitTimerRef = useRef<number | null>(null);

  const closeWithExit = useCallback(() => {
    if (exiting) return;
    if (prefersReducedMotion()) {
      onClose();
      return;
    }
    setExiting(true);
    exitTimerRef.current = window.setTimeout(() => {
      exitTimerRef.current = null;
      onClose();
    }, POPOVER_EXIT_MS);
  }, [exiting, onClose]);

  // Reset the local `exiting` flag ONLY on a real false→true `open`
  // transition — parent just reopened the popover, so a fresh mount
  // must not inherit the leftover exit class from the previous close
  // and play the fade-out instead of the fade-in. A prev-value ref
  // does the transition detection: reacting to `open && exiting`
  // directly would also fire on the FIRST render after `closeWithExit`
  // (which sets `exiting=true` while `open` is still `true`, since the
  // parent's own unmount is deliberately delayed until our timer runs)
  // — and immediately reset the flag we just set, killing the animation.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (!prevOpenRef.current && open) {
      setExiting(false);
      if (exitTimerRef.current !== null) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    }
    prevOpenRef.current = open;
  }, [open]);

  useEffect(
    () => () => {
      if (exitTimerRef.current !== null) clearTimeout(exitTimerRef.current);
    },
    []
  );

  useClickOutside([wrapperRef, popoverRef], closeWithExit, open);
  useCloseOnLeave([wrapperRef, popoverRef], open && closeOnLeave, closeWithExit);

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      {/* `Tooltip` replaces the browser's native `title` attribute here
          (audit finding — ~1 s delay was real friction on icon-only
          triggers like Landing's Ajustes gear). Native `title=` is
          intentionally NOT set on the button; leaving both would flash a
          second, slower tooltip next to the custom one. */}
      <Tooltip label={triggerTitle}>
        <button
          className={triggerClassName}
          onClick={onToggle}
          aria-label={triggerLabel}
          aria-haspopup="true"
          aria-expanded={open}
        >
          {triggerIcon}
        </button>
      </Tooltip>
      {open && (
        <Popover
          anchorRef={wrapperRef}
          popoverRef={popoverRef}
          onClose={closeWithExit}
          align={align}
          className={exiting ? `${popoverClassName} popover-exiting` : popoverClassName}
        >
          {children}
        </Popover>
      )}
    </div>
  );
}

/** One `.project-menu-item` row inside a `<DropdownMenu>`'s popover. */
export function DropdownMenuItem({
  icon,
  label,
  active,
  danger,
  onClick,
}: {
  icon?: ReactNode;
  label: ReactNode;
  active?: boolean;
  danger?: boolean;
  onClick: () => void;
}) {
  const className = [
    "button",
    "project-menu-item",
    active && "tab-active",
    danger && "button-danger",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button className={className} onClick={onClick}>
      {icon}
      {label}
    </button>
  );
}
